import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import { useModalResize } from '../hooks/useModalResize';
import ModalHeader from './ui/ModalHeader';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';

/** localStorage key the modal's chosen size + maximized state live under. */
const CHASSIS_MAP_SIZE_KEY = 'chassisLocationMapSize';

function createChassisMarkerElement(unit: string): HTMLDivElement {
  const container = document.createElement('div');
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.alignItems = 'center';
  container.innerHTML = `
    <div style="color:#000;font-size:11px;font-weight:700;font-family:var(--app-font-family);white-space:nowrap;margin-bottom:-1px;text-shadow:-1px -1px 0 #fff,1px -1px 0 #fff,-1px 1px 0 #fff,1px 1px 0 #fff,0 -1px 0 #fff,0 1px 0 #fff,-1px 0 0 #fff,1px 0 0 #fff;">${unit}</div>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 100" width="80" height="16">
      <path fill="#1e3a5f" d="M464.06,86.32,490.21,39.97l-484.71-.72c0-3.64,0,0,0,0,0,3.64,2.18,13.96,5.83,13.96h31.12c-2.87,4.41-4.63,9.71-4.63,15.45,0,15.78,12.91,28.69,28.69,28.69,8.94,0,16.77-4.19,22.07-10.59,5.3,6.4,13.13,10.59,22.07,10.59s16.77-4.19,22.07-10.59c5.3,6.4,13.13,10.59,22.07,10.59,15.78,0,28.69-12.91,28.69-28.69,0-5.74-1.77-11.03-4.63-15.45h24.5v15.45c0,3.64,2.98,6.62,6.62,6.62h121.38c3.64,0,6.62-2.98,6.62-6.62v-15.45h145.06M66.5,84.22c-8.5,0-15.45-6.95-15.45-15.45s6.95-15.45,15.45-15.45,15.45,6.95,15.45,15.45-6.95,15.45-15.45,15.45ZM110.63,84.22c-8.5,0-15.45-6.95-15.45-15.45s6.95-15.45,15.45-15.45,15.45,6.95,15.45,15.45-6.95,15.45-15.45,15.45ZM170.22,68.77c0,8.5-6.95,15.45-15.45,15.45s-15.45-6.95-15.45-15.45,6.95-15.45,15.45-15.45,15.45,6.95,15.45,15.45ZM324.7,62.15h-108.14v-8.83h108.14v8.83Z"/>
    </svg>
  `;
  return container;
}

function ChassisLocationMap({ lat, lng, unit }: { lat: number; lng: number; unit: string }) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [mapType, setMapType] = useState<'roadmap' | 'hybrid'>('roadmap');

  const center = useMemo(() => ({ lat, lng }), [lat, lng]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    const chassisElement = createChassisMarkerElement(unit);
    markerRef.current = new google.maps.marker.AdvancedMarkerElement({
      position: { lat, lng },
      map,
      content: chassisElement,
      title: unit,
      zIndex: 100,
    });
  }, [lat, lng, unit]);

  useEffect(() => {
    return () => {
      if (markerRef.current) markerRef.current.map = null;
    };
  }, []);

  return (
    <div className="relative h-full w-full">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={12}
        onLoad={onMapLoad}
        options={{
          mapId: '3cf64d0fcebdaf11f3625d6e',
          mapTypeId: mapType,
          disableDefaultUI: true,
          zoomControl: true,
          // Centred on the right edge so the zoom buttons clear the panel's
          // bottom-right resize grip.
          zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        }}
      />
      {/* Satellite Toggle */}
      <button
        onClick={() => setMapType(prev => (prev === 'roadmap' ? 'hybrid' : 'roadmap'))}
        className="absolute top-3 left-3 bg-panel hover:bg-gray-100 dark:hover:bg-gray-700 rounded shadow-md p-2 z-10"
        title={mapType === 'roadmap' ? 'Switch to Satellite view' : 'Switch to Map view'}
        aria-label={mapType === 'roadmap' ? 'Switch to Satellite view' : 'Switch to Map view'}
      >
        {mapType === 'roadmap' ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-500 dark:text-gray-300">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-500 dark:text-gray-300">
            <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
            <path d="M9 3v15" />
            <path d="M15 6v15" />
          </svg>
        )}
      </button>
    </div>
  );
}

export interface ChassisLocationMapModalProps {
  unit: string;
  lat: number;
  lng: number;
  onClose: () => void;
}

/**
 * Portal modal that displays a single chassis's GPS location on a Google Map.
 * Self-loads the Maps JS API and renders nothing until it is ready.
 */
const ChassisLocationMapModal: React.FC<ChassisLocationMapModalProps> = ({ unit, lat, lng, onClose }) => {
  const { isLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // Rendered only while open, so the layer claim is unconditional. Read-only
  // map: Alt+C closes, and the layer keeps Alt+S off the grid underneath.
  useAltHotkeys({ C: onClose }, undefined, { layer: true });

  // Unmounted by the parent when closed, so `true` is fine — unmount resets state.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);
  const { panelSizeStyle, isMaximized, toggleMaximized, resizeHandleProps } = useModalResize({
    active: true,
    storageKey: CHASSIS_MAP_SIZE_KEY,
    // Matches the pre-resize size: half the viewport wide, and tall enough that
    // the map itself still gets roughly its old 50vh.
    defaultWidth: 0.5,
    defaultHeight: 0.55,
    minWidth: 420,
    minHeight: 320,
    panelRef,
  });

  if (!isLoaded) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-scrim/50 flex items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        data-testid="chassis-map-panel"
        style={{ ...panelStyle, ...panelSizeStyle }}
        className="relative flex flex-col bg-panel rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={unit}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          actions={
            <button
              type="button"
              onClick={toggleMaximized}
              className="relative rounded-md text-slate-300 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
              title={isMaximized ? 'Restore size' : 'Maximize'}
            >
              <span className="absolute -inset-2.5"></span>
              <span className="sr-only">{isMaximized ? 'Restore size' : 'Maximize'}</span>
              {isMaximized ? (
                <ArrowsPointingInIcon className="size-5" aria-hidden="true" />
              ) : (
                <ArrowsPointingOutIcon className="size-5" aria-hidden="true" />
              )}
            </button>
          }
        />
        <div className="min-h-0 flex-1">
          <ChassisLocationMap lat={lat} lng={lng} unit={unit} />
        </div>
        {/* Resize grip — drag to resize; the panel grows about its centre. */}
        <div
          {...resizeHandleProps}
          role="separator"
          aria-label="Resize map"
          title="Drag to resize"
          className="absolute bottom-0 right-0 z-20 h-4 w-4 cursor-nwse-resize touch-none select-none rounded-tl bg-white/80 dark:bg-gray-800/80"
        >
          <svg viewBox="0 0 16 16" className="h-full w-full text-gray-500 dark:text-gray-300" aria-hidden="true">
            <path d="M15 5 L5 15 M15 10 L10 15" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChassisLocationMapModal;
